<template>
  <v-card class="documents-dangling-card mt-8" outlined>
    <v-card-title class="d-flex align-center py-4 px-6 cursor-pointer" @click="expanded = !expanded">
      <v-icon color="warning" class="mr-3">mdi-file-alert-outline</v-icon>
      <div>
        <div class="d-flex align-center">
          <span class="text-subtitle-1 font-weight-bold text-white">[DEV] {{ $t('documents.dangling') }}</span>
          <v-chip x-small class="ml-3 font-weight-bold" color="yellowTransp" text-color="orangeColor">
            {{ records.length }} records &middot; {{ documentCount }} files
          </v-chip>
        </div>
        <div class="caption grey--text text--lighten-1 mt-1 font-weight-regular">
          {{ $t('documents.danglingHint') }}
        </div>
      </div>

      <v-spacer />

      <v-btn icon small @click.stop="expanded = !expanded">
        <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>
    </v-card-title>

    <v-expand-transition>
      <div v-show="expanded">
        <v-divider class="dangling-divider" />
        <v-data-table
          class="elevation-0 documents-dangling__table"
          :headers="headers"
          :items="records"
          item-key="record_id"
          sort-by="last_upload"
          sort-desc
          :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
          @click:row="$emit('select', $event)"
        >
          <template #[`item.kind`]="{ item }">
            <v-chip
              x-small
              :color="item.kind === 'kyc' ? 'aliveTransp' : 'primaryTransp'"
              :text-color="item.kind === 'kyc' ? 'alive' : 'primary'"
              class="font-weight-medium text-capitalize"
            >
              {{ item.kind === 'kyc' ? $t('documents.kycTab') : $t('documents.operationsTab') }}
            </v-chip>
          </template>

          <template #[`item.record_id`]="{ item }">
            <div class="d-flex flex-column py-1">
              <span v-if="item.alias" class="font-weight-bold white--text">{{ item.alias }}</span>
              <copyable-id :id="item.record_id" :type="item.kind" />
            </div>
          </template>

          <template #[`item.company_id`]="{ item }">
            <div class="d-flex align-center">
              <copyable-id :id="item.company_id" type="company" />
            </div>
          </template>

          <template #[`item.broker_name`]="{ item }">
            <span :class="{ 'grey--text text--darken-1': !item.broker_name }">
              {{ item.broker_name || '-' }}
            </span>
          </template>

          <template #[`item.document_count`]="{ item }">
            <v-chip x-small outlined class="font-weight-medium">
              {{ item.document_count }}
            </v-chip>
          </template>

          <template #[`item.last_upload`]="{ item }">
            <date-time-layout v-if="item.last_upload" :timestamp="item.last_upload" />
            <span v-else class="grey--text">-</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              small
              outlined
              color="primary"
              class="open-btn text-capitalize"
              @click.stop="$emit('select', item)"
            >
              {{ $t('documents.open') }}
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import CopyableId from '@/components/common/CopyableId.vue'
import DateTimeLayout from '@/components/common/DateTimeLayout.vue'

export default {
  name: 'DocumentsDanglingSection',

  components: { CopyableId, DateTimeLayout },

  props: {
    // Records whose company_id resolves to nothing. Root broker only: they
    // belong to no broker, so there is nobody else they could be scoped to.
    records: { type: Array, default: () => [] },
  },

  emits: ['select'],

  data () {
    return { expanded: false }
  },

  computed: {
    documentCount () {
      return this.records.reduce((total, record) => total + (record.document_count || 0), 0)
    },
    headers () {
      return [
        { text: this.$t('documents.danglingKind'),      value: 'kind' },
        { text: this.$t('documents.danglingRecord'),    value: 'record_id' },
        { text: this.$t('documents.documents'),         value: 'document_count', align: 'center' },
        { text: this.$t('documents.danglingCompanyId'), value: 'company_id' },
        { text: this.$t('documents.broker'),            value: 'broker_name' },
        { text: this.$t('documents.uploadDate'),        value: 'last_upload' },
        { text: '',                                     value: 'actions', sortable: false, align: 'end' },
      ]
    },
  },
}
</script>

<style scoped lang="scss">
.documents-dangling-card {
  background: #1e1e1e !important;
  border: 1px solid rgba(255, 170, 0, 0.25) !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.cursor-pointer {
  cursor: pointer;
}

.dangling-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.documents-dangling__table {
  background: transparent !important;

  ::v-deep tbody tr {
    cursor: pointer;
    transition: background 0.15s ease-in-out;

    &:hover {
      background: rgba(255, 255, 255, 0.04) !important;
    }
  }

  ::v-deep thead th {
    font-size: 0.8rem !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.6) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
    white-space: nowrap !important;

    .v-data-table-header__icon {
      display: inline-flex !important;
      vertical-align: middle !important;
      margin-left: 4px !important;
    }
  }
}

.orphan-tag {
  font-size: 9px !important;
  height: 16px !important;
  padding: 0 4px !important;
}

.open-btn {
  border-color: rgba(0, 115, 255, 0.4) !important;
  font-size: 11px !important;
  font-weight: 600;

  &:hover {
    background: rgba(0, 115, 255, 0.1) !important;
    border-color: #0073ff !important;
  }
}
</style>
